import { useSubscription } from "@apollo/client/react";
import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import { Headline } from "@volumental/common";
import { toDataURL } from "qrcode";
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { getJourneyIdFromUrl } from "@volumental/journey-manager";
import { Population } from "../../../api/gql/graphql";
import type { Gbswl } from "../../../api/gql/graphql";
import { reconstructionEvents } from "../../../api/subscriptions/reconstruction-events";
import { useInputStore, useScannerStore, useSessionStore, useViewStore } from "../../../store";
import type { ScanResult, UserFlowType } from "../../../types/types";
import { getScannerUrl } from "../../../util/get-scanner-url";

interface QrCodeProps {
  onScan: (scan: ScanResult, survey?: Gbswl) => void;
  flowType: UserFlowType;
}

export function QrCode({ onScan, flowType }: QrCodeProps): JSX.Element {
  const jid = getJourneyIdFromUrl();
  const sideBarOpen = useViewStore((store) => store.sideBarOpen);
  const { token, deviceId, paperType, skipOnboarding } = useSessionStore((store) => store);
  const { previousPurchaseID, email } = useInputStore((store) => store);
  const { disableCaptureValidation } = useScannerStore((store) => store);
  const [session] = useState(Math.random().toString(36).slice(-6));
  const [qrcode, setQrcode] = useState("");
  const [invalidQr, setInvalidQr] = useState(false);

  const { t, i18n } = useTranslation();

  useSubscription(reconstructionEvents, {
    variables: { session, gender: Population.Unisex },
    onData: (d) => {
      const { data } = d.data;
      if (data && "scan" in data.reconstruction) {
        const { scan } = data.reconstruction;
        const measurement = scan.measurement;
        if (measurement) {
          onScan({
            type: scan.__typename,
            id: scan.id,
            publicID: scan.publicID,
            measurement,
            createdAt: scan.createdAt as string,
            meshes: {
              leftFoot: scan.meshes?.leftFoot ?? "",
              rightFoot: scan.meshes?.rightFoot ?? "",
            },
            claimed: scan.claimed,
          });
        }
      }
    },
  });

  const paperFormat = paperType === "us-letter" ? "letter" : (paperType ?? undefined);
  const scannerUrl = getScannerUrl(flowType as UserFlowType.Solo | UserFlowType.Assisted | UserFlowType.AssistedAdult, {
    clientId: token,
    deviceId: deviceId ?? "",
    currentUrl: globalThis.location.href,
    previousPurchaseId: previousPurchaseID ?? undefined,
    session,
    paperFormat: skipOnboarding && paperFormat ? paperFormat : undefined,
    skipValidation: disableCaptureValidation,
    language: i18n.language,
    journeyId: jid ?? undefined,
    email: email ?? undefined,
  });

  useEffect(() => {
    setInvalidQr(false);
    setQrcode("");
    if (!scannerUrl) return;
    toDataURL(scannerUrl, (err, imageData) => {
      if (err) {
        setInvalidQr(true);
        return;
      }
      setQrcode(imageData);
    });
  }, [scannerUrl]);

  useEffect(() => {
    if (sideBarOpen && qrcode) {
      usageTracker.track(UsageTrackerEvent.ShowQrCode);
    }
  }, [sideBarOpen, qrcode]);

  return (
    <>
      {Boolean(qrcode) &&
        (import.meta.env.DEV ? (
          <a href={scannerUrl} target="_blank" rel="noopener noreferrer">
            <img className="w-50 rounded-2xl" src={qrcode} alt={t("imageAlt.qr")} />
          </a>
        ) : (
          <img className="w-50 rounded-2xl" src={qrcode} alt={t("imageAlt.qr")} />
        ))}
      {Boolean(invalidQr) && (
        <>
          <Headline>{t("pleaseOpenMobile")}</Headline>
          <div className="font-normal text-base text-center mt-2.5">{t("findYourBestSize")}</div>
        </>
      )}
    </>
  );
}
